import { Input, Modal, Radio, Select } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import { FOOD_SAFETY_SECONDARY_TEXT } from 'src/constants/foodSafety'
import FoodSafetyClockField from 'src/components/food-safety/FoodSafetyClockField'
import {
  FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH,
  FOOD_SAFETY_OUT_OF_SERVICE_START,
  describeFoodSafetyOutOfServiceStartEffect,
} from 'src/utils/foodSafetyOutOfService'

const START = FOOD_SAFETY_OUT_OF_SERVICE_START

/** Reason dialog for `useFoodSafetyOutOfService`. */
const FoodSafetyOutOfServiceDialog = ({ outOfService }) => {
  const {
    unit,
    reason,
    started,
    startDate,
    startTime,
    backdateReason,
    backdated,
    dayOptions = [],
    canManage,
    todayLocalDate,
    error,
    errorField,
    saving,
  } = outOfService
  const earlier = started === START.EARLIER
  const effect = earlier
    ? describeFoodSafetyOutOfServiceStartEffect({
        dueByTime: unit?.dueByTime,
        localDate: startDate,
        time: startTime,
        todayLocalDate,
      })
    : null
  const submitOnEnter = e => {
    if (!e.shiftKey) {
      e.preventDefault()
      outOfService.confirmMark()
    }
  }
  const errorAt = field =>
    error && errorField === field ? (
      <ErrorText role='alert' data-testid='fs-out-of-service-error'>
        {error}
      </ErrorText>
    ) : null

  return (
    <Modal
      visible={Boolean(unit)}
      title={unit ? `Mark ${unit.name} out of service?` : ''}
      okText='Mark out of service'
      cancelText='Cancel'
      confirmLoading={saving}
      onOk={outOfService.confirmMark}
      onCancel={outOfService.closeMark}
      afterClose={outOfService.afterClose}
      destroyOnClose
    >
      <Body data-testid='fs-out-of-service-dialog'>
        <Hint data-testid='fs-out-of-service-hint'>
          No readings are asked for until it’s returned to service. A day’s
          check isn’t counted as missed if the unit was already out at the
          check’s due time. Readings already logged stay as they are.
        </Hint>
        <label htmlFor='fs-out-of-service-reason'>
          <strong>Why?</strong>
        </label>
        <Input.TextArea
          id='fs-out-of-service-reason'
          autoFocus
          rows={2}
          value={reason}
          maxLength={FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH}
          placeholder='e.g. Compressor failed — engineer booked'
          onChange={e => outOfService.updateReason(e.target.value)}
          onPressEnter={submitOnEnter}
          data-testid='fs-out-of-service-reason'
        />
        {errorAt('reason')}

        <Field role='group' aria-labelledby='fs-out-of-service-start-label'>
          <strong id='fs-out-of-service-start-label'>
            When did it stop working?
          </strong>
          <Radio.Group
            value={started}
            onChange={e => outOfService.updateStarted(e.target.value)}
            data-testid='fs-out-of-service-started'
          >
            <Radio value={START.NOW} data-testid='fs-out-of-service-now'>
              Now
            </Radio>
            <Radio
              value={START.EARLIER}
              data-testid='fs-out-of-service-earlier'
            >
              Earlier
            </Radio>
          </Radio.Group>
        </Field>

        {earlier ? (
          <Earlier data-testid='fs-out-of-service-earlier-fields'>
            <Row>
              {dayOptions.length > 1 ? (
                <Select
                  value={startDate}
                  onChange={outOfService.updateStartDate}
                  style={{ minWidth: '9rem' }}
                  aria-label='Day it stopped working'
                  data-testid='fs-out-of-service-day'
                >
                  {dayOptions.map(option => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              ) : (
                <DayText>Today</DayText>
              )}
              <FoodSafetyClockField
                value={startTime}
                aria-label='Time it stopped working'
                onChange={outOfService.updateStartTime}
                onPressEnter={submitOnEnter}
                data-testid='fs-out-of-service-time'
              />
            </Row>
            {errorAt('time')}
            {effect ? (
              <Hint data-testid='fs-out-of-service-effect'>{effect}</Hint>
            ) : null}
            {!canManage ? (
              <Hint data-testid='fs-out-of-service-manager-note'>
                Only a manager can record an earlier day.
              </Hint>
            ) : null}
            {backdated ? (
              <>
                <label htmlFor='fs-out-of-service-backdate-reason'>
                  <strong>Why is it being recorded now?</strong>
                </label>
                <Input.TextArea
                  id='fs-out-of-service-backdate-reason'
                  rows={2}
                  value={backdateReason}
                  maxLength={FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH}
                  placeholder='e.g. Found it off this morning — engineer report came late'
                  onChange={e =>
                    outOfService.updateBackdateReason(e.target.value)
                  }
                  data-testid='fs-out-of-service-backdate-reason'
                />
                <Hint>Kept with the out-of-service record.</Hint>
                {errorAt('backdateReason')}
              </>
            ) : null}
          </Earlier>
        ) : null}

        {error && !errorField ? errorAt(null) : null}
      </Body>
    </Modal>
  )
}

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-top: 0.5rem;
`

const Earlier = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const DayText = styled.span`
  font-weight: 600;
`

const Hint = styled.p`
  margin: 0;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ErrorText = styled.p`
  margin: 0;
  color: #a8071a;
`

export default FoodSafetyOutOfServiceDialog
